import { getApiBaseUrl } from "../config/runtimeConfig";
import { ApiError } from "../obras/obrasApi";

const apiBaseUrl = getApiBaseUrl();

// Módulo hand-written para a cadeia auto→aprovação→fatura→pagamento ao
// subempreiteiro [4.12.15]. Reutiliza o ApiError de obrasApi (que humaniza o
// ProblemDetails do backend) e replica o helper request<> mínimo — o request
// de obrasApi é privado ao módulo, por isso não é importável.
async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

export { ApiError };

export type SubcontractorAutoStatus = "draft" | "approved" | "invoiced" | "paid";

export interface SubcontractorAutoDto {
  id: number;
  workId: number;
  workSubcontractorItemId: number | null;
  subcontractorName: string;
  number: string;
  status: SubcontractorAutoStatus;
  measuredAmount: number;
  approvedAt: string | null;
  approvedByUserId: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  invoiceAmount: number | null;
  retentionPercent: number;
  retentionAmount: number;
  netPayable: number;
  paidAmount: number;
  paidAt: string | null;
  notes: string | null;
  currencyCode: string;
  created: string;
  lastModified: string;
}

export interface SubcontractorRetentionDto {
  subcontractorName: string;
  totalRetention: number;
  autoCount: number;
}

export interface SubcontractorRetentionsDto {
  bySubcontractor: SubcontractorRetentionDto[];
  totalRetention: number;
}

export interface CreateSubcontractorAutoPayload {
  workSubcontractorItemId: number;
  measuredAmount?: number;
  retentionPercent?: number;
  notes?: string | null;
}

export interface UpdateSubcontractorAutoPayload {
  measuredAmount: number;
  retentionPercent: number;
  notes?: string | null;
}

export interface RegisterSubcontractorInvoicePayload {
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: number;
}

export interface RegisterSubcontractorPaymentPayload {
  amount: number;
  paidAt?: string | null;
}

export function getSubcontractorAutos(
  workId: number,
): Promise<SubcontractorAutoDto[]> {
  return request<SubcontractorAutoDto[]>(
    `/api/works/${workId}/subcontractor-autos`,
  );
}

export function getSubcontractorRetentions(
  workId: number,
): Promise<SubcontractorRetentionsDto> {
  return request<SubcontractorRetentionsDto>(
    `/api/works/${workId}/subcontractor-retentions`,
  );
}

export function createSubcontractorAuto(
  workId: number,
  payload: CreateSubcontractorAutoPayload,
): Promise<SubcontractorAutoDto> {
  return request<SubcontractorAutoDto>(
    `/api/works/${workId}/subcontractor-autos`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function updateSubcontractorAuto(
  workId: number,
  autoId: number,
  payload: UpdateSubcontractorAutoPayload,
): Promise<SubcontractorAutoDto> {
  return request<SubcontractorAutoDto>(
    `/api/works/${workId}/subcontractor-autos/${autoId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export async function deleteSubcontractorAuto(
  workId: number,
  autoId: number,
): Promise<void> {
  const response = await fetch(
    `${apiBaseUrl}/api/works/${workId}/subcontractor-autos/${autoId}`,
    { method: "DELETE", credentials: "include" },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}

export function approveSubcontractorAuto(
  workId: number,
  autoId: number,
): Promise<SubcontractorAutoDto> {
  return request<SubcontractorAutoDto>(
    `/api/works/${workId}/subcontractor-autos/${autoId}/approve`,
    { method: "POST" },
  );
}

export function reopenSubcontractorAuto(
  workId: number,
  autoId: number,
): Promise<SubcontractorAutoDto> {
  return request<SubcontractorAutoDto>(
    `/api/works/${workId}/subcontractor-autos/${autoId}/reopen`,
    { method: "POST" },
  );
}

export function registerSubcontractorInvoice(
  workId: number,
  autoId: number,
  payload: RegisterSubcontractorInvoicePayload,
): Promise<SubcontractorAutoDto> {
  return request<SubcontractorAutoDto>(
    `/api/works/${workId}/subcontractor-autos/${autoId}/invoice`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export function registerSubcontractorPayment(
  workId: number,
  autoId: number,
  payload: RegisterSubcontractorPaymentPayload,
): Promise<SubcontractorAutoDto> {
  return request<SubcontractorAutoDto>(
    `/api/works/${workId}/subcontractor-autos/${autoId}/payment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}
